import { GameState } from '../game/GameState';
import type { DoctrineData, InstitutionData, TechnologyData, TechnologyCategory } from '../types/data';
import type { FactionResearchState, InstitutionState, ResearchFocus } from '../types/game';
import { DataRegistry } from './DataRegistry';
import { ModifierManager } from './ModifierManager';

const DEFAULT_RESEARCH: FactionResearchState = {
  knowledge: 0,
  monthlyKnowledge: 0,
  focus: 'balanced',
  activeProgress: 0,
  scholars: 0,
  catchUpBonus: 0,
  diffusionProgress: {},
  institutionProgress: {},
  adoptedInstitutions: [],
  doctrineTradition: {},
};

export class TechnologyManager {
  static readonly instance = new TechnologyManager();

  ensureCampaignInitialized() {
    const campaign = GameState.instance.requireCampaign();
    campaign.institutions ??= {};
    campaign.technologyHistory ??= [];

    for (const institution of DataRegistry.instance.institutionList()) {
      campaign.institutions[institution.id] ??= this.createInstitutionState(institution);
    }

    for (const faction of Object.values(campaign.factions)) {
      faction.research ??= this.freshResearchState();
      faction.research.diffusionProgress ??= {};
      faction.research.institutionProgress ??= {};
      faction.research.adoptedInstitutions ??= [];
      faction.research.doctrineTradition ??= {};
      faction.research.knowledge ??= 0;
      faction.research.monthlyKnowledge ??= 0;
      faction.research.focus ??= 'balanced';
      faction.research.activeProgress ??= 0;
      faction.research.scholars ??= 0;
      faction.research.catchUpBonus ??= 0;
      faction.counterIntelligence ??= 20;

      for (const institution of DataRegistry.instance.institutionList()) {
        faction.research.institutionProgress[institution.id] ??= this.initialInstitutionProgress(faction.id, institution);
        if (this.shouldStartAdopted(institution) && !faction.research.adoptedInstitutions.includes(institution.id)) {
          faction.research.adoptedInstitutions.push(institution.id);
          faction.research.institutionProgress[institution.id] = 100;
          const state = campaign.institutions[institution.id];
          if (state && !state.adoptedBy.includes(faction.id)) state.adoptedBy.push(faction.id);
        }
      }
    }
  }

  processMonth() {
    this.ensureCampaignInitialized();
    this.processInstitutionDiscoveryAndSpread();
    const campaign = GameState.instance.requireCampaign();
    for (const faction of Object.values(campaign.factions)) {
      this.processResearch(faction.id);
      this.processInstitutionProgress(faction.id);
      this.processDoctrineTradition(faction.id);
      if (faction.id !== campaign.playerFactionId) this.processAi(faction.id);
    }
  }

  has(factionId: string, technologyId: string) {
    return GameState.instance.requireCampaign().factions[factionId]?.researchedTechs.includes(technologyId) ?? false;
  }

  availableTechnologies(factionId: string) {
    this.ensureCampaignInitialized();
    return DataRegistry.instance.technologyList().filter((technology) => this.canResearch(factionId, technology.id));
  }

  canResearch(factionId: string, technologyId: string) {
    const campaign = GameState.instance.requireCampaign();
    const faction = campaign.factions[factionId];
    const technology = DataRegistry.instance.technologies[technologyId];
    if (!faction || !technology || faction.researchedTechs.includes(technologyId)) return false;
    if ((technology.prerequisites ?? []).some((prerequisite) => !faction.researchedTechs.includes(prerequisite))) return false;
    if (technology.requiredInstitution && !faction.research.adoptedInstitutions.includes(technology.requiredInstitution)) return false;
    return true;
  }

  selectResearch(factionId: string, technologyId: string) {
    this.ensureCampaignInitialized();
    const campaign = GameState.instance.requireCampaign();
    const faction = campaign.factions[factionId];
    const technology = DataRegistry.instance.technologies[technologyId];
    if (!faction || !technology || !this.canResearch(factionId, technologyId)) {
      GameState.instance.log(`${technology?.name ?? technologyId} is not currently available.`, 'warning');
      return false;
    }
    if (faction.research.activeTechnologyId !== technologyId) faction.research.activeProgress = Math.min(faction.research.activeProgress, this.cost(factionId, technologyId) * 0.2);
    faction.research.activeTechnologyId = technologyId;
    GameState.instance.log(`${DataRegistry.instance.factions[factionId]?.name ?? factionId} began researching ${technology.name}.`, factionId === campaign.playerFactionId ? 'success' : 'info');
    return true;
  }

  research(factionId: string, technologyId: string) {
    return this.selectResearch(factionId, technologyId);
  }

  setFocus(factionId: string, focus: ResearchFocus) {
    this.ensureCampaignInitialized();
    const faction = GameState.instance.requireCampaign().factions[factionId];
    if (!faction) return false;
    faction.research.focus = focus;
    return true;
  }

  sponsorScholar(factionId: string) {
    this.ensureCampaignInitialized();
    const campaign = GameState.instance.requireCampaign();
    const faction = campaign.factions[factionId];
    if (!faction || faction.research.scholars >= 5) return false;
    const cost = 55 + faction.research.scholars * 25;
    if (faction.treasury < cost) {
      GameState.instance.log(`A new scholar requires ${cost} gold in patronage.`, 'warning');
      return false;
    }
    faction.treasury -= cost;
    faction.research.scholars++;
    faction.prestige = Math.min(100, faction.prestige + 2);
    campaign.technologyHistory.unshift({ id: `scholar_${factionId}_${campaign.turn}_${faction.research.scholars}`, factionId, turn: campaign.turn, type: 'scholar', title: `A new scholar joined the ${DataRegistry.instance.factions[factionId]?.name ?? factionId} court.` });
    campaign.technologyHistory = campaign.technologyHistory.slice(0, 80);
    GameState.instance.log(`Scholar patronage expanded the court's research circle.`, 'success');
    return true;
  }

  cost(factionId: string, technologyId: string) {
    const technology = DataRegistry.instance.technologies[technologyId];
    const faction = GameState.instance.requireCampaign().factions[factionId];
    if (!technology || !faction) return 0;
    const diffusion = Math.min(35, (faction.research.diffusionProgress[technologyId] ?? 0) * 0.35);
    const catchUp = Math.min(20, faction.research.catchUpBonus * 100);
    return Math.max(20, Math.round(technology.cost * (1 - (diffusion + catchUp) / 100)));
  }

  progress(factionId: string, technologyId?: string) {
    const faction = GameState.instance.requireCampaign().factions[factionId];
    if (!faction) return 0;
    const id = technologyId ?? faction.research.activeTechnologyId;
    if (!id) return 0;
    return Math.max(0, Math.min(100, Math.round(faction.research.activeProgress / Math.max(1, this.cost(factionId, id)) * 100)));
  }

  adoptInstitution(factionId: string, institutionId: string) {
    this.ensureCampaignInitialized();
    const campaign = GameState.instance.requireCampaign();
    const faction = campaign.factions[factionId];
    const institution = DataRegistry.instance.institutions[institutionId];
    const state = campaign.institutions[institutionId];
    if (!faction || !institution || !state || faction.research.adoptedInstitutions.includes(institutionId)) return false;
    if ((faction.research.institutionProgress[institutionId] ?? 0) < 100) {
      GameState.instance.log(`${institution.name} has not spread far enough to adopt.`, 'warning');
      return false;
    }
    const cost = this.institutionCost(factionId, institutionId);
    if (faction.treasury < cost) {
      GameState.instance.log(`Adopting ${institution.name} requires ${cost} gold.`, 'warning');
      return false;
    }
    faction.treasury -= cost;
    faction.research.adoptedInstitutions.push(institutionId);
    if (!state.adoptedBy.includes(factionId)) state.adoptedBy.push(factionId);
    faction.prestige = Math.min(100, faction.prestige + 4);
    campaign.technologyHistory.unshift({ id: `institution_${institutionId}_${factionId}_${campaign.turn}`, factionId, institutionId, turn: campaign.turn, type: 'institution', title: `${DataRegistry.instance.factions[factionId]?.name ?? factionId} adopted ${institution.name}.` });
    campaign.technologyHistory = campaign.technologyHistory.slice(0, 80);
    GameState.instance.log(`${institution.name} adopted.`, 'success');
    return true;
  }

  institutionCost(factionId: string, institutionId: string) {
    const faction = GameState.instance.requireCampaign().factions[factionId];
    const institution = DataRegistry.instance.institutions[institutionId];
    if (!faction || !institution) return 0;
    const progressDiscount = Math.min(0.25, Math.max(0, (faction.research.institutionProgress[institutionId] ?? 0) - 100) / 200);
    return Math.max(25, Math.round(institution.adoptionCost * (1 - progressDiscount)));
  }

  adoptDoctrine(factionId: string, doctrineId: string) {
    this.ensureCampaignInitialized();
    const campaign = GameState.instance.requireCampaign();
    const faction = campaign.factions[factionId];
    const doctrine = DataRegistry.instance.doctrines[doctrineId];
    if (!faction || !doctrine || !this.doctrineAvailable(factionId, doctrineId)) return false;
    const current = doctrine.branch === 'land' ? faction.research.landDoctrineId : faction.research.navalDoctrineId;
    if (current === doctrineId) return true;
    const cost = current ? 75 : 45;
    if (faction.treasury < cost) {
      GameState.instance.log(`Adopting ${doctrine.name} requires ${cost} gold.`, 'warning');
      return false;
    }
    faction.treasury -= cost;
    if (doctrine.branch === 'land') faction.research.landDoctrineId = doctrineId;
    else faction.research.navalDoctrineId = doctrineId;
    faction.research.doctrineTradition[doctrineId] ??= 0;
    campaign.technologyHistory.unshift({ id: `doctrine_${doctrineId}_${factionId}_${campaign.turn}`, factionId, doctrineId, turn: campaign.turn, type: 'doctrine', title: `${DataRegistry.instance.factions[factionId]?.name ?? factionId} adopted ${doctrine.name}.` });
    campaign.technologyHistory = campaign.technologyHistory.slice(0, 80);
    GameState.instance.log(`${doctrine.name} adopted as ${doctrine.branch} doctrine.`, 'success');
    return true;
  }

  doctrineAvailable(factionId: string, doctrineId: string) {
    const faction = GameState.instance.requireCampaign().factions[factionId];
    const doctrine = DataRegistry.instance.doctrines[doctrineId];
    return Boolean(faction && doctrine && (doctrine.requiredTechs ?? []).every((technologyId) => faction.researchedTechs.includes(technologyId)));
  }

  private processResearch(factionId: string) {
    const campaign = GameState.instance.requireCampaign();
    const faction = campaign.factions[factionId];
    if (!faction) return;
    const gain = this.monthlyKnowledge(factionId);
    faction.research.monthlyKnowledge = gain;
    faction.research.knowledge += gain;
    this.processDiffusion(factionId);
    const technologyId = faction.research.activeTechnologyId;
    if (!technologyId || !this.canResearch(factionId, technologyId)) return;
    const technology = DataRegistry.instance.technologies[technologyId];
    const focusMultiplier = this.focusMultiplier(faction.research.focus, technology.category);
    faction.research.activeProgress += gain * focusMultiplier;
    if (faction.research.activeProgress + 0.001 < this.cost(factionId, technologyId)) return;
    faction.research.activeProgress = Math.max(0, faction.research.activeProgress - this.cost(factionId, technologyId));
    faction.researchedTechs.push(technologyId);
    faction.research.activeTechnologyId = undefined;
    faction.prestige = Math.min(100, faction.prestige + 3 + technology.tier);
    campaign.technologyHistory.unshift({ id: `research_${technologyId}_${factionId}_${campaign.turn}`, factionId, technologyId, turn: campaign.turn, type: 'research', title: `${DataRegistry.instance.factions[factionId]?.name ?? factionId} completed ${technology.name}.` });
    campaign.technologyHistory = campaign.technologyHistory.slice(0, 80);
    GameState.instance.log(`${technology.name} research completed.`, factionId === campaign.playerFactionId ? 'success' : 'info');
  }

  private monthlyKnowledge(factionId: string) {
    const campaign = GameState.instance.requireCampaign();
    const faction = campaign.factions[factionId];
    if (!faction) return 0;
    const modifiers = ModifierManager.instance.forFaction(factionId);
    const advisors = Object.values(faction.advisorSlots).filter(Boolean).length;
    const schools = Object.values(campaign.provinces).filter((province) => province.owner === factionId && province.buildings.some((buildingId) => /temple|school|academy|mosque|library/.test(buildingId))).length;
    const base = 7 + faction.research.scholars * 2.4 + advisors * 0.45 + schools * 0.6 + faction.prestige / 35;
    return Math.max(1, Math.round(base * (1 + modifiers.researchSpeedBonus + faction.research.catchUpBonus) * 10) / 10);
  }

  private processDiffusion(factionId: string) {
    const campaign = GameState.instance.requireCampaign();
    const faction = campaign.factions[factionId];
    if (!faction) return;
    const partners = new Set([...(faction.treaties.trade ?? []), ...(faction.treaties.alliances ?? []), ...(faction.treaties.marriages ?? [])]);
    for (const technology of DataRegistry.instance.technologyList()) {
      if (faction.researchedTechs.includes(technology.id)) continue;
      const knownByPartners = [...partners].filter((partnerId) => campaign.factions[partnerId]?.researchedTechs.includes(technology.id)).length;
      const globallyKnown = Object.values(campaign.factions).filter((other) => other.researchedTechs.includes(technology.id)).length;
      if (!knownByPartners && globallyKnown < Math.max(2, Math.ceil(Object.keys(campaign.factions).length * 0.3))) continue;
      const gain = knownByPartners * 0.9 + globallyKnown * 0.08 + faction.research.scholars * 0.05;
      faction.research.diffusionProgress[technology.id] = Math.min(100, (faction.research.diffusionProgress[technology.id] ?? 0) + gain);
    }
    const averageKnown = Object.values(campaign.factions).reduce((sum, other) => sum + other.researchedTechs.length, 0) / Math.max(1, Object.keys(campaign.factions).length);
    faction.research.catchUpBonus = Math.max(0, Math.min(0.25, (averageKnown - faction.researchedTechs.length) * 0.018));
  }

  private processInstitutionDiscoveryAndSpread() {
    const campaign = GameState.instance.requireCampaign();
    for (const institution of DataRegistry.instance.institutionList()) {
      const state = campaign.institutions[institution.id];
      if (!state || campaign.turn < state.discoveredTurn) continue;
      state.globalSpread = Math.min(100, state.globalSpread + institution.spreadRate * (1 + state.adoptedBy.length * 0.08));
      state.lastSpreadTurn = campaign.turn;
    }
  }

  private processInstitutionProgress(factionId: string) {
    const campaign = GameState.instance.requireCampaign();
    const faction = campaign.factions[factionId];
    if (!faction) return;
    const modifiers = ModifierManager.instance.forFaction(factionId);
    const ports = Object.values(campaign.provinces).filter((province) => province.owner === factionId && DataRegistry.instance.provinces[province.id]?.port).length;
    const partners = new Set([...(faction.treaties.trade ?? []), ...(faction.treaties.alliances ?? [])]);
    for (const institution of DataRegistry.instance.institutionList()) {
      const state = campaign.institutions[institution.id];
      if (!state || campaign.turn < state.discoveredTurn || faction.research.adoptedInstitutions.includes(institution.id)) continue;
      const partnerAdopters = [...partners].filter((partnerId) => campaign.factions[partnerId]?.research.adoptedInstitutions.includes(institution.id)).length;
      const originBonus = state.originFactionId === factionId ? 2.5 : 0;
      const gain = institution.spreadRate * 0.45 + state.globalSpread * 0.012 + partnerAdopters * 1.25 + ports * 0.16 + faction.research.scholars * 0.18 + originBonus;
      faction.research.institutionProgress[institution.id] = Math.min(125, (faction.research.institutionProgress[institution.id] ?? 0) + gain * (1 + modifiers.institutionSpreadBonus));
    }
  }

  private processDoctrineTradition(factionId: string) {
    const faction = GameState.instance.requireCampaign().factions[factionId];
    if (!faction) return;
    if (faction.research.landDoctrineId) faction.research.doctrineTradition[faction.research.landDoctrineId] = Math.min(100, (faction.research.doctrineTradition[faction.research.landDoctrineId] ?? 0) + faction.militaryTradition * 0.01);
    if (faction.research.navalDoctrineId) faction.research.doctrineTradition[faction.research.navalDoctrineId] = Math.min(100, (faction.research.doctrineTradition[faction.research.navalDoctrineId] ?? 0) + faction.navalTradition * 0.01);
  }

  private processAi(factionId: string) {
    const campaign = GameState.instance.requireCampaign();
    const faction = campaign.factions[factionId];
    const data = DataRegistry.instance.factions[factionId];
    if (!faction || !data) return;
    if (!faction.research.activeTechnologyId) {
      const preferred = this.preferredCategories(data.aiPersonality);
      const available = this.availableTechnologies(factionId).sort((a, b) => {
        const aPreference = preferred.indexOf(a.category);
        const bPreference = preferred.indexOf(b.category);
        return (aPreference < 0 ? 9 : aPreference) - (bPreference < 0 ? 9 : bPreference) || a.tier - b.tier || a.cost - b.cost;
      });
      if (available[0]) this.selectResearch(factionId, available[0].id);
    }
    if (campaign.turn % 4 === 0 && faction.research.scholars < 3 && faction.treasury > 180) this.sponsorScholar(factionId);
    for (const institution of DataRegistry.instance.institutionList()) {
      if (!faction.research.adoptedInstitutions.includes(institution.id) && (faction.research.institutionProgress[institution.id] ?? 0) >= 100 && faction.treasury >= this.institutionCost(factionId, institution.id) + 60) {
        this.adoptInstitution(factionId, institution.id);
        break;
      }
    }
    if (!faction.research.landDoctrineId) {
      const doctrine = DataRegistry.instance.doctrineList().find((entry) => entry.branch === 'land' && this.doctrineAvailable(factionId, entry.id));
      if (doctrine && faction.treasury > 100) this.adoptDoctrine(factionId, doctrine.id);
    }
    if (!faction.research.navalDoctrineId && ['sea_raider', 'merchant_opportunist', 'colonial_strategist'].includes(data.aiPersonality)) {
      const doctrine = DataRegistry.instance.doctrineList().find((entry) => entry.branch === 'naval' && this.doctrineAvailable(factionId, entry.id));
      if (doctrine && faction.treasury > 100) this.adoptDoctrine(factionId, doctrine.id);
    }
  }

  private createInstitutionState(institution: InstitutionData): InstitutionState {
    const campaign = GameState.instance.requireCampaign();
    const monthsUntilOrigin = Math.max(0, (institution.originYear - campaign.year) * 12 - (campaign.month - 1));
    const discoveredTurn = campaign.turn + monthsUntilOrigin;
    const originFactionId = this.originFactionForInstitution(institution.id);
    const originProvinceId = Object.values(campaign.provinces).find((province) => province.owner === originFactionId)?.id;
    return { id: institution.id, discoveredTurn, originFactionId, originProvinceId, globalSpread: campaign.year >= institution.originYear ? Math.min(100, 18 + (campaign.year - institution.originYear) * 1.5) : 0, adoptedBy: [], lastSpreadTurn: campaign.turn };
  }

  private originFactionForInstitution(institutionId: string) {
    const preferred: Record<string, string[]> = {
      temple_schools: ['majapahit', 'ming', 'demak'],
      maritime_exchange: ['malacca', 'arab_confederation', 'aceh'],
      gunpowder_revolution: ['ming', 'portuguese', 'demak'],
      print_culture: ['ming', 'portuguese', 'dutch_voc'],
      chartered_company_system: ['dutch_voc', 'british_eic', 'portuguese'],
      scientific_method: ['dutch_voc', 'british_eic', 'ming'],
    };
    return (preferred[institutionId] ?? []).find((factionId) => GameState.instance.requireCampaign().factions[factionId]) ?? Object.keys(GameState.instance.requireCampaign().factions)[0];
  }

  private initialInstitutionProgress(factionId: string, institution: InstitutionData) {
    const campaign = GameState.instance.requireCampaign();
    if (campaign.year < institution.originYear) return 0;
    const factionData = DataRegistry.instance.factions[factionId];
    const ports = Object.values(campaign.provinces).filter((province) => province.owner === factionId && DataRegistry.instance.provinces[province.id]?.port).length;
    const age = campaign.year - institution.originYear;
    const maritimeBonus = /maritime|company/.test(institution.id) && ports ? 18 : 0;
    const personalityBonus = ['merchant_opportunist', 'colonial_strategist'].includes(factionData?.aiPersonality ?? '') ? 10 : 0;
    return Math.min(100, 12 + age * institution.spreadRate * 0.7 + maritimeBonus + personalityBonus);
  }

  private shouldStartAdopted(institution: InstitutionData) {
    const campaign = GameState.instance.requireCampaign();
    return campaign.year >= institution.originYear + (institution.era === 'foundations' ? 80 : institution.era === 'regional' ? 70 : institution.era === 'gunpowder' ? 60 : 45);
  }

  private focusMultiplier(focus: ResearchFocus, category: TechnologyCategory) {
    if (focus === 'balanced') return 1;
    return focus === category ? 1.22 : 0.9;
  }

  private preferredCategories(personality: string): TechnologyCategory[] {
    if (['sea_raider', 'merchant_opportunist', 'colonial_strategist'].includes(personality)) return ['naval', 'economy', 'administration', 'military', 'culture'];
    if (['imperial_expansionist', 'devout_unifier'].includes(personality)) return ['military', 'administration', 'culture', 'economy', 'naval'];
    if (['defensive_isolationist', 'legitimist_monarch'].includes(personality)) return ['administration', 'culture', 'military', 'economy', 'naval'];
    return ['economy', 'administration', 'culture', 'military', 'naval'];
  }

  private freshResearchState(): FactionResearchState {
    return { ...DEFAULT_RESEARCH, diffusionProgress: {}, institutionProgress: {}, adoptedInstitutions: [], doctrineTradition: {} };
  }
}